/**
 * GestureConsentDialog — Pre-camera privacy consent dialog.
 *
 * Uses the shared ViewerModalFrame design system for consistency with
 * other viewer modals (Parts, Tools, Tutorial, Help, Share).
 * Shown before getUserMedia to satisfy Requirements 8.1, 8.2, 1.6.
 */

import { ViewerModalFrame, ViewerModalSection, type ViewerModalTheme } from '../UI/viewer-modals/ViewerModalFrame';

interface GestureConsentDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  theme?: ViewerModalTheme;
}

export function GestureConsentDialog({ open, onConfirm, onCancel, theme }: GestureConsentDialogProps) {
  return (
    <ViewerModalFrame
      open={open}
      onClose={onCancel}
      title="Enable Hand Gesture Controls"
      titleIcon="front_hand"
      theme={theme}
      ariaLabel="Enable hand gesture controls consent dialog"
    >
      <ViewerModalSection title="Privacy" theme={theme}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: '1.25rem', color: 'rgba(56, 189, 248, 0.9)', flexShrink: 0, marginTop: 2 }}
            >
              security
            </span>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.5 }}>
              Your camera feed is processed <strong style={{ color: 'rgba(255, 255, 255, 0.95)' }}>entirely on your device</strong>. 
              Video is never transmitted to any server and is not recorded or stored.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: '1.25rem', color: 'rgba(56, 189, 248, 0.9)', flexShrink: 0, marginTop: 2 }}
            >
              gesture
            </span>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.5 }}>
              Use hand gestures to navigate assembly steps hands-free — swipe, thumbs up, 
              fist, and more.
            </p>
          </div>
        </div>
      </ViewerModalSection>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.5rem' }}>
        <button
          onClick={onCancel}
          style={{
            padding: '0.625rem 1.25rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'transparent',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.85rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{
            padding: '0.625rem 1.25rem',
            borderRadius: '0.75rem',
            border: 'none',
            background: 'linear-gradient(135deg, #FD936E 0%, #8181FF 100%)',
            color: '#FFFFFF',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '1.1rem', fontWeight: 300 }}>
              videocam
            </span>
            Enable Camera
          </span>
        </button>
      </div>
    </ViewerModalFrame>
  );
}
